import React, { useState, useRef } from 'react';
import { Box, TextField } from '@mui/material';
import Modal from '../Modal';
import CountrySelector from '../CountrySelector';
import ClientPartyTypeSelector from '../ClientPartyTypeSelector';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import { COUNTRY_RETURN_TYPES, CLIENT_PARTY_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

const ClientModal = ({ 
  isOpen, 
  onClose, 
  onClientCreated, 
  initialData = null,
  isEdit = false 
}) => {
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingCountry, setIsCreatingCountry] = useState(false); // Track country creation state
  const [pendingSubmission, setPendingSubmission] = useState(false); // Track if we're waiting to submit after country creation
  const [formData, setFormData] = useState({
    name: '',
    countryId: '',
    partyType: ''
  });
  const modalInitialized = useRef(false);

  // Update form data when initialData changes
  React.useEffect(() => {
    if (isOpen && !modalInitialized.current) {
      modalInitialized.current = true;
      
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          countryId: initialData.countryId || '',
          partyType: initialData.partyType || ''
        });
      } else {
        setFormData({
          name: '',
          countryId: '',
          partyType: ''
        });
      }
    } else if (!isOpen) {
      // Reset initialization flag when modal closes
      modalInitialized.current = false;
    }
  }, [isOpen, initialData]);

  // Auto-submit when country is selected after creation
  React.useEffect(() => {
    if (pendingSubmission && formData.countryId && !isCreatingCountry) {
      setPendingSubmission(false);
      
      // Use setTimeout to ensure this runs after current execution cycle
      setTimeout(() => {
        handleSave(new Event('submit'));
      }, 50);
    }
  }, [formData.countryId, pendingSubmission, isCreatingCountry]);

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Check if we're in the middle of creating a country
    if (isCreatingCountry) {
      return;
    }
    
    // Check if any modal dialogs are open (backup check)
    const isCreatingCountryModal = document.querySelector('.MuiModal-root [role="dialog"]'); // Country creation modal
    if (isCreatingCountryModal) {
      return;
    }
    
    if (!formData.name.trim()) {
      showAlert('Client name is required', 'error');
      return;
    }
    
    if (!formData.countryId) {
      // If we're not currently creating a country, this is a real validation error
      if (!isCreatingCountry) {
        // Check if we just finished creating a country and should wait for state update
        setPendingSubmission(true);
        return;
      }
      
      showAlert('Country is required', 'error');
      return;
    }
    
    if (!formData.partyType) {
      showAlert('Party type is required', 'error');
      return;
    }
    
    // Clear any pending submission flag
    setPendingSubmission(false);
    
    try {
      setIsLoading(true);
      
      let savedClient;
      if (isEdit && initialData?.id) {
        // Edit existing client
        savedClient = await organizationApi.updateClient(initialData.id, {
          name: formData.name,
          countryId: formData.countryId,
          partyType: formData.partyType
        });
        showAlert('Client updated successfully', 'success');
      } else {
        // Add new client
        savedClient = await organizationApi.createClient({
          name: formData.name,
          countryId: formData.countryId,
          partyType: formData.partyType
        });
        showAlert('Client created successfully', 'success');
      }
      
      // Notify parent component
      if (onClientCreated) {
        onClientCreated(savedClient);
      }
      
      // Reset form and close modal
      setFormData({ name: '', countryId: '', partyType: '' });
      onClose();
    } catch (err) {
      const action = isEdit ? 'update' : 'create';
      showAlert(err.message || `Failed to ${action} client.`, 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form data
    setFormData({ name: '', countryId: '', partyType: '' });
    onClose();
  };

  // Check if form is valid for submit button state
  const isFormValid = formData.name.trim() && formData.countryId && formData.partyType;

  // Handle keydown events to prevent form submission when creating countries
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      const target = event.target;
      // Check if the Enter key is pressed inside a CountrySelector input
      const isCountrySelector = target.closest('[data-testid="country-selector"]') || 
                               target.closest('.MuiAutocomplete-root');
      
      if (isCountrySelector) {
        // Check if this would create a new country (no matching options)
        const inputElement = target.closest('.MuiAutocomplete-root')?.querySelector('input');
        if (inputElement && inputElement.value.trim()) {
          // Get the CountrySelector component to check if this would create a new country
          // For now, prevent form submission to let CountrySelector handle it
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSave}
      title={isEdit ? "Edit Client" : "Add New Client"}
      submitText={isEdit ? "Update Client" : "Create Client"}
      submitDisabled={isLoading || !isFormValid}
      onKeyDown={handleKeyDown}
    >
      <Box mb={2}>
        <TextField
          label="Client Name"
          required
          value={formData.name}
          onChange={(e) => setFormData(prev => ({
            ...prev, 
            name: e.target.value
          }))}
          placeholder="Enter client name"
          autoComplete="off"
          fullWidth
          variant="outlined"
          disabled={isLoading}
        />
      </Box>

      <Box mb={2}>
        <CountrySelector
          value={formData.countryId}
          onChange={(value) => {
            setFormData(prev => {
              const newFormData = {
                ...prev,
                countryId: value
              };
              return newFormData;
            });
          }}
          onCreatingCountry={setIsCreatingCountry}
          returnValue={COUNTRY_RETURN_TYPES.ID}
          required
          fullWidth
          disabled={isLoading}
          data-testid="country-selector"
        />
      </Box>

      <Box mb={2}>
        <ClientPartyTypeSelector
          value={formData.partyType}
          onChange={(value) => {
            setFormData(prev => ({
              ...prev,
              partyType: value
            }));
          }}
          returnValue={CLIENT_PARTY_TYPE_RETURN_TYPES.ID}
          label="Party Type"
          required={true}
          fullWidth
          disabled={isLoading}
          showEmptyOption={false}
        />
      </Box>
    </Modal>
  );
};

export default ClientModal; 